/**
 * Tabs — primitive accesible y controlado/no-controlado.
 *
 * Uso (no-controlado):
 *   <Tabs defaultValue="a">
 *     <TabsList>
 *       <TabsTrigger value="a" icon={<Icon />}>Tab A</TabsTrigger>
 *       <TabsTrigger value="b">Tab B</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="a">…</TabsContent>
 *     <TabsContent value="b">…</TabsContent>
 *   </Tabs>
 *
 * Uso controlado: pasar `value` + `onValueChange`.
 *
 * Diseño:
 *  - Pill-row con scroll-x en mobile (≤640px) para no romper layout.
 *  - Active = brand-50 bg + brand-700 text + brand-500 underline.
 *  - Idle  = fg-muted text, hover → bg-bg-muted.
 *  - Foco visible (focus-visible:ring) por accesibilidad.
 *  - ARIA: role=tablist/tab/tabpanel, aria-selected, aria-controls.
 */
import { createContext, useCallback, useContext, useId, useMemo, useState } from 'react';
import { cn } from '../../utils/cn';

const TabsCtx = createContext(null);

function useTabs() {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error('TabsTrigger/TabsContent must be used inside <Tabs>');
  return ctx;
}

export function Tabs({ value, defaultValue, onValueChange, children, className }) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const current = isControlled ? value : internal;
  // setCurrent entra en las deps del contexto: sin eso los hijos seguían llamando al
  // onValueChange del último cambio de pestaña, y un consumidor que lee su estado ahí
  // (p. ej. un guard de "cambios sin guardar") veía un valor viejo.
  const setCurrent = useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );
  const baseId = useId().replace(/:/g, '');
  const ctx = useMemo(() => ({ current, setCurrent, baseId }), [current, setCurrent, baseId]);
  return (
    <TabsCtx.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsCtx.Provider>
  );
}

export function TabsList({ children, className, ariaLabel }) {
  // Roving tabindex: los triggers inactivos llevan tabIndex=-1, así que sin
  // esto el teclado no podía cambiar de pestaña (patrón ARIA tabs: ←/→ mueven
  // foco y activan; Home/End saltan a los extremos).
  const onKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const tabs = [...e.currentTarget.querySelectorAll('[role="tab"]:not([disabled])')];
    const i = tabs.indexOf(document.activeElement);
    if (i === -1 || tabs.length === 0) return;
    e.preventDefault();
    const next =
      e.key === 'Home'
        ? tabs[0]
        : e.key === 'End'
          ? tabs[tabs.length - 1]
          : tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    next.focus();
    next.click();
  };
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn(
        'flex items-center gap-1 border-b border-border',
        '-mx-1 px-1 overflow-x-auto md:overflow-visible',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({ value, icon, children, disabled, className }) {
  const { current, setCurrent, baseId } = useTabs();
  const active = current === value;
  return (
    <button
      type="button"
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-selected={active}
      aria-controls={`${baseId}-panel-${value}`}
      tabIndex={active ? 0 : -1}
      disabled={disabled}
      onClick={() => setCurrent(value)}
      className={cn(
        // shrink-0: un trigger jamás se encoge bajo su contenido — con muchos
        // tabs se activa el overflow-x del TabsList, no el solape de labels.
        'relative inline-flex shrink-0 items-center gap-1.5 h-9 px-3 text-sm font-medium whitespace-nowrap',
        'rounded-t-md transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        active
          ? 'text-brand-700 bg-brand-50'
          : 'text-fg-muted hover:text-fg hover:bg-bg-muted',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      {icon && (
        <span className="[&>svg]:w-3.5 [&>svg]:h-3.5 inline-flex items-center" aria-hidden>
          {icon}
        </span>
      )}
      <span>{children}</span>
      {active && (
        <span
          className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-brand-500"
          aria-hidden
        />
      )}
    </button>
  );
}

export function TabsContent({ value, children, className }) {
  const { current, baseId } = useTabs();
  if (current !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      tabIndex={0}
      className={cn('focus:outline-none', className)}
    >
      {children}
    </div>
  );
}
